import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Globe, Plus, CheckCircle2, Edit2, Save, X, Sparkles, AlertCircle } from 'lucide-react';
import { DomainStatus } from '@ftt/shared';

export const AdminDomainsPage: React.FC = () => {
  const [editingDomain, setEditingDomain] = useState<any>(null);
  const [domainName, setDomainName] = useState('');
  const [status, setStatus] = useState<DomainStatus>('UNDER_EVALUATION');
  const [registrar, setRegistrar] = useState('');
  const [notes, setNotes] = useState('');
  const [isPreferred, setIsPreferred] = useState(false);

  const queryClient = useQueryClient();

  const { data: domainsRes, isLoading } = useQuery({
    queryKey: ['admin-domains'],
    queryFn: () => api.get('/admin/domains').then((res) => res.data.data),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      return api.post('/admin/domains', {
        id: editingDomain?.id || undefined,
        domainName,
        status,
        registrar,
        notes,
        isPreferred,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-domains'] });
      setEditingDomain(null);
    },
  });

  const domains = domainsRes || [];

  const openEditor = (dom?: any) => {
    if (dom) {
      setEditingDomain(dom);
      setDomainName(dom.domainName);
      setStatus(dom.status);
      setRegistrar(dom.registrar || '');
      setNotes(dom.notes || '');
      setIsPreferred(dom.isPreferred);
    } else {
      setEditingDomain({ id: '' });
      setDomainName('');
      setStatus('UNDER_EVALUATION');
      setRegistrar('Hostinger');
      setNotes('');
      setIsPreferred(false);
    }
  };

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
            Domain Name Planning &amp; Evaluation
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Internal evaluation matrix for domain naming strategies, brand acquisition candidates, and registrar notes.
          </p>
        </div>

        <button
          onClick={() => openEditor()}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-sm transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Add Domain Candidate</span>
        </button>
      </div>

      {/* Notice */}
      <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-400 space-y-1">
        <div className="flex items-center gap-2 text-brand-400 font-bold uppercase font-mono text-[10px]">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Internal Planning Only</span>
        </div>
        <p>
          This module is an internal research repository for assessing candidate domains before purchasing via Hostinger or approved registrars. It is never displayed publicly.
        </p>
      </div>

      {/* Domain Cards Grid */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading domain candidates...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {domains.map((dom: any) => (
            <div
              key={dom.id}
              className={`p-6 rounded-2xl bg-slate-950 border transition-all space-y-4 flex flex-col justify-between ${
                dom.isPreferred ? 'border-brand-500/80 ring-1 ring-brand-500/30' : 'border-slate-800'
              }`}
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-mono font-bold uppercase px-2.5 py-1 rounded ${
                    dom.status === 'RECOMMENDED'
                      ? 'bg-brand-900/80 text-brand-300 border border-brand-500/40'
                      : dom.status === 'AVAILABLE'
                      ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                      : 'bg-slate-900 text-slate-400 border border-slate-800'
                  }`}>
                    {dom.status.replace('_', ' ')}
                  </span>
                  {dom.isPreferred && (
                    <span className="text-[10px] font-mono uppercase font-bold text-brand-400">
                      ★ Preferred Choice
                    </span>
                  )}
                </div>

                <h3 className="font-mono text-lg font-bold text-white tracking-wider">
                  {dom.domainName}
                </h3>

                <div className="text-xs text-slate-400 space-y-1">
                  <div>Registrar: <strong className="text-slate-200">{dom.registrar || 'Hostinger'}</strong></div>
                  {dom.notes && <p className="text-slate-300 italic pt-1">&ldquo;{dom.notes}&rdquo;</p>}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-800 flex items-center justify-between text-xs">
                <span className="text-[10px] font-mono text-slate-500">
                  Checked: {dom.checkedDate ? new Date(dom.checkedDate).toLocaleDateString() : 'Pending'}
                </span>
                <button
                  onClick={() => openEditor(dom)}
                  className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-white font-bold inline-flex items-center gap-1 border border-slate-700"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  <span>Update</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {editingDomain && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-950 rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-800">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-serif text-xl font-bold text-white">
                {editingDomain.id ? 'Update Domain Candidate' : 'Add Domain Candidate'}
              </h3>
              <button onClick={() => setEditingDomain(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Domain Name *
                </label>
                <input
                  type="text"
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                  placeholder="e.g. forthetruthpublishing.com"
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white font-mono focus:outline-hidden"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-400 uppercase font-mono mb-1">Evaluation Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as any)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                  >
                    <option value="UNDER_EVALUATION">UNDER_EVALUATION</option>
                    <option value="AVAILABLE">AVAILABLE</option>
                    <option value="RECOMMENDED">RECOMMENDED</option>
                    <option value="TAKEN">TAKEN</option>
                    <option value="PURCHASED">PURCHASED</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-400 uppercase font-mono mb-1">Registrar</label>
                  <input
                    type="text"
                    value={registrar}
                    onChange={(e) => setRegistrar(e.target.value)}
                    placeholder="Hostinger / Namecheap"
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">Internal Notes &amp; Rationale</label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Strategic purpose, keywords, price, or branding notes..."
                  className="w-full px-3.5 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="pref"
                  checked={isPreferred}
                  onChange={(e) => setIsPreferred(e.target.checked)}
                  className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-brand-500 focus:ring-brand-500"
                />
                <label htmlFor="pref" className="text-xs text-slate-300 select-none">
                  Mark as Preferred Candidate for Publishing Website
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => setEditingDomain(null)}
                className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={!domainName || saveMutation.isPending}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs"
              >
                Save Domain Candidate
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
